/**
 * ClipGStreamViewerXR.jsx — WebXR (Quest / iOS AR) 版的 ClipGStream 播放器。
 *
 * 為什麼要另開一個檔:原生 ClipGStreamViewer 用 WebGPU 光柵化,而 WebXR 沉浸式
 * session 只吃 WebGL framebuffer(WebGPU 的 XRGPUBinding 目前 production 尚未支援)。
 * 所以 XR 版走「解碼在 WebGPU、算圖在 WebGL(three.js + Spark)」的混合架構:
 *
 *   1. 用 ClipGStream 的 decoder(decoder.js)以 **CPU readback** 模式 decode()
 *      預解碼每一幀 → 得到 ply-ready 的 17-float 高斯記錄(Float32Array)。
 *   2. 每幀轉成一個 in-memory .ply → Spark SplatMesh(three.js 物件)。
 *   3. 場景/相機/XR 全部沿用 DualgsViewer 的 three.js + WebXR 骨架
 *      (DualgsViewer-XR.jsx 的模組是通用的)。
 *
 * 記憶體策略(關鍵):
 *   - PC(桌面):維持「全預載」—— 一次把所有幀解成 SplatMesh 常駐,吃記憶體換
 *     零延遲換幀。桌面有足夠 RAM,行為與最初版本相同(保持原本程式碼)。
 *   - 非 PC(Quest / iPhone / 行動裝置):走「串流 + 有限快取」—— 只保留目前
 *     播放點附近的少數幀(maxCachedFrames),播過即 dispose 釋放,記憶體固定不
 *     再線性累加(否則手機解碼到一半就 OOM 閃退)。播放 fps 上限鎖 24。
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { SplatMesh } from '@sparkjsdev/spark'
import { createDecoder, createSharedDevice, setAssetVersion } from './decoder.js'
import {
  useXRSupport,
  useXRSession,
  setupXRControllers,
  XRButton,
  ModelTransformPanel,
  loadVariantLaunchSDK,
} from '../DualgsViewer/DualgsViewer-XR.jsx'

// 串流路徑的播放 fps 上限(行動裝置解碼較慢,鎖 24 讓體驗穩定)。
const STREAM_FPS_CAP = 24

// ── 裝置判定:非 PC(Quest / 手機 / 平板)走串流路徑;其餘視為 PC 走全預載 ──
// Quest 瀏覽器 UA 帶 OculusBrowser;iPadOS 13+ 會偽裝成 Macintosh,用觸控點補判。
// 也給 ModelViewer 用來決定 clipgstream 要走桌面 WebGPU 版還是這支串流版。
export function isLimitedDevice() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/OculusBrowser|Quest|Pico|PicoBrowser|VR|XR/i.test(ua)) return true
  if (/iPhone|iPad|iPod|Android|Mobile/i.test(ua)) return true
  if (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1) return true // iPad
  return false
}

// ── frames 解析(同 ClipGStreamViewer)─────────────────────────────────────
function parseFrames(str) {
  const out = []
  for (const tok of String(str).split(',')) {
    const t = tok.trim(); if (!t) continue
    const m = t.match(/^(\d+)\s*-\s*(\d+)$/)
    if (m) { for (let i = Math.min(+m[1], +m[2]); i <= Math.max(+m[1], +m[2]); i++) out.push(i) }
    else { const n = parseInt(t); if (!isNaN(n)) out.push(n) }
  }
  return out
}

// ── 17-float 高斯記錄 → 標準 3DGS .ply(binary_little_endian)→ Spark SplatMesh ──
// decoder 輸出佈局(見 renderer.js 檔頭):
//   [0..2] xyz  [3..5] normals(=0)  [6..8] f_dc  [9] opacity_logit
//   [10..12] scale_log  [13..16] quat(w,x,y,z) —— 正好是無 SH-rest 的 3DGS ply。
const PLY_PROPS = [
  'x', 'y', 'z', 'nx', 'ny', 'nz',
  'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity',
  'scale_0', 'scale_1', 'scale_2',
  'rot_0', 'rot_1', 'rot_2', 'rot_3',
]

// 一幀的 verts → SplatMesh(Blob URL 餵給 Spark,與 DualgsViewer 的 { url } 相同路徑)。
// 直接用 Blob([headerBytes, body]) 拼接,body 是 verts.buffer 的 view —— 少一次把
// header+body 合併成新 Uint8Array 的 ~200MB 暫態複製(行動裝置的記憶體尖峰很致命)。
function vertsToSplatMesh(verts, count) {
  return new Promise((resolve, reject) => {
    let header = 'ply\nformat binary_little_endian 1.0\n'
    header += `element vertex ${count}\n`
    for (const p of PLY_PROPS) header += `property float ${p}\n`
    header += 'end_header\n'
    const headerBytes = new TextEncoder().encode(header)
    const body = new Uint8Array(verts.buffer, verts.byteOffset, count * 17 * 4)
    const url = URL.createObjectURL(new Blob([headerBytes, body], { type: 'application/octet-stream' }))
    const mesh = new SplatMesh({
      url,
      onLoad: () => { URL.revokeObjectURL(url); resolve(mesh) },
      onError: (e) => { URL.revokeObjectURL(url); reject(e) },
    })
  })
}

// ── 依不透明度降採樣到 target 顆(行動裝置用,直接砍立體算圖的 fill-rate)──
// 每顆 17 float,index 9 = opacity_logit(越大越不透明)。用直方圖找一個門檻,
// 保留最不透明的 ~target 顆 —— 低不透明度的 splat 對畫面貢獻小卻照吃 fill-rate,
// 優先砍掉它們。O(count) 三趟(min/max → 直方圖 → 複製),不做全排序(Quest CPU 便宜)。
function downsampleByOpacity(verts, count, target) {
  if (!target || count <= target) return { verts, count }
  const S = 17, OP = 9
  let mn = Infinity, mx = -Infinity
  for (let i = 0; i < count; i++) { const o = verts[i * S + OP]; if (o < mn) mn = o; if (o > mx) mx = o }
  const out = new Float32Array(target * S)
  let w = 0
  if (mx <= mn) {
    // 不透明度全同 → 退回等距抽樣
    const stride = count / target
    for (let k = 0; k < target; k++) { const i = Math.min(count - 1, Math.floor(k * stride)); out.set(verts.subarray(i * S, i * S + S), w++ * S) }
    return { verts: out, count: w }
  }
  const B = 256
  const hist = new Uint32Array(B)
  const scale = (B - 1) / (mx - mn)
  for (let i = 0; i < count; i++) hist[((verts[i * S + OP] - mn) * scale) | 0]++
  // 從最不透明那端往下累加,湊到 target 的那個 bin 當門檻
  let acc = 0, thrBin = 0
  for (let b = B - 1; b >= 0; b--) { acc += hist[b]; if (acc >= target) { thrBin = b; break } }
  for (let i = 0; i < count && w < target; i++) {
    if (((verts[i * S + OP] - mn) * scale | 0) >= thrBin) { out.set(verts.subarray(i * S, i * S + S), w++ * S) }
  }
  return { verts: out, count: w }
}

// ── SuperSplat grounding(讓模型落在 AR reticle 上,同 DualgsViewer)─────────
const MODEL_AR_OFFSET = [0, 0, 0]
const MODEL_AR_ROTATION = [0, 0, 0]

const ClipGStreamViewerXR = forwardRef(function ClipGStreamViewerXR({
  className,
  dataUrl,                 // 資料集根目錄(同 ClipGStreamViewer)
  gframes = null,          // 要播放的 global frames;不指定則涵蓋 manifest 全部 clip
  fps: fpsProp = 30,       // PC 播放 fps;串流路徑會被 STREAM_FPS_CAP(24)壓上限
  keep = 0.05,
  enableXR = true,
  showUi = true,
  maxCachedFrames = 12,    // 串流路徑同時常駐的幀數上限(僅非 PC 裝置生效)
  targetSplats = 250000,   // 非 PC:decode 後依不透明度降採樣到此上限(0 = 不降)
  limited: limitedProp = null,  // 由 ModelViewer 傳入的穩健裝置判定;null 則退回 UA
}, ref) {
  const containerRef = useRef(null)
  const rendererRef = useRef(null)
  const sceneRef = useRef(null)
  const cameraRef = useRef(null)
  const controlsRef = useRef(null)
  const modelAnchorRef = useRef(null)
  const arOverlayRef = useRef(null)

  const framesRef = useRef([])        // PC 全預載:SplatMesh[]
  const curFrameMeshRef = useRef(null)
  const curIdxRef = useRef(0)
  const playingRef = useRef(true)
  const animIdRef = useRef(null)
  const canvasElRef = useRef(null)

  const [isLoading, setIsLoading] = useState(true)
  const [status, setStatus] = useState('loading…')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState(null)
  const [totalFrames, setTotalFrames] = useState(0)
  const [arStatus, setArStatus] = useState(null)
  const [gpuInfo, setGpuInfo] = useState(null)   // { text, sw } —— adapter 資訊

  // 裝置分流結果:優先用 ModelViewer 傳入的 limited(它用 WebXR 支援度做穩健判定,
  // 能正確辨識 UA 被凍結的 Quest);沒傳才退回 UA。非 PC → 串流 24fps;PC → 全預載。
  const limited = limitedProp != null ? limitedProp : isLimitedDevice()

  // ── XR:全部沿用 DualgsViewer-XR 的通用模組 ────────────────────────────
  const xrSupport = useXRSupport(enableXR)
  const { xrActive, enterXR, exitXR, grabRef: xrGrabRef } = useXRSession({
    rendererRef,
    canvasRef: containerRef,
    modelAnchorRef,
    overlayRef: arOverlayRef,
  })
  useEffect(() => { if (enableXR) loadVariantLaunchSDK() }, [enableXR])

  useImperativeHandle(ref, () => ({
    getCanvas: () => canvasElRef.current,
    togglePlay: () => { playingRef.current = !playingRef.current },
  }), [])

  const gframesKey = Array.isArray(gframes) ? gframes.join(',') : (gframes ?? '')

  // ── 主 effect:three.js 場景 + XR 接線 + (全預載 | 串流)+ 播放迴圈 ─────────
  useEffect(() => {
    if (!dataUrl || !containerRef.current) return
    // limited(非 PC → 串流;PC → 全預載)由元件層決定,見上方 const limited
    let disposed = false
    let device = null
    const container = containerRef.current
    const builtMeshes = []                 // PC 路徑 build 的 SplatMesh(cleanup 釋放)
    const streamCache = new Map()          // 串流路徑:index -> SplatMesh
    let streamCurDec = null                // 串流路徑目前存活的 clip decoder

    const log = (m) => { if (!disposed) setStatus(m) }

    // ---- three.js 場景(骨架同 DualgsViewer) ----
    const scene = new THREE.Scene(); sceneRef.current = scene
    const modelAnchor = new THREE.Group(); scene.add(modelAnchor); modelAnchorRef.current = modelAnchor

    const renderer = new THREE.WebGLRenderer({ preserveDrawingBuffer: true, antialias: true, alpha: true })
    renderer.setClearColor(0x000000, 1)
    renderer.setSize(1, 1)
    container.appendChild(renderer.domElement)
    rendererRef.current = renderer
    canvasElRef.current = renderer.domElement

    const { updateXrGrab } = setupXRControllers({
      renderer, scene, modelAnchor, enableXR,
      grabRef: xrGrabRef, onArStatus: setArStatus,
      arOffset: MODEL_AR_OFFSET, arRotation: MODEL_AR_ROTATION,
    })

    const camera = new THREE.PerspectiveCamera(50, 1, 0.01, 1000)
    camera.position.set(0, 0, 2.5)
    cameraRef.current = camera
    const controls = new OrbitControls(camera, renderer.domElement)
    controls.target.set(0, 0, 0)
    controls.update()
    controlsRef.current = controls

    const resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      if (width === 0 || height === 0) return
      camera.aspect = width / height
      camera.updateProjectionMatrix()
      renderer.setSize(width, height)
    })
    resizeObserver.observe(container)

    // modelAnchor 上換上指定 mesh(移除前一顆,不 dispose —— 釋放由各路徑自理)
    function showMesh(mesh) {
      if (!mesh || mesh === curFrameMeshRef.current) return
      const prev = curFrameMeshRef.current
      if (prev && prev !== mesh) modelAnchor.remove(prev)
      modelAnchor.add(mesh)
      curFrameMeshRef.current = mesh
    }

    // 一幀 global frame → SplatMesh(含座標翻正)。串流/全預載共用。
    async function buildFrameMesh(dec, gf, CS) {
      const t = (gf % CS) / CS
      const raw = await dec.decode(t, keep)   // CPU readback → { verts, count }
      // 非 PC:decode 後把單幀降到 targetSplats(砍立體算圖 fill-rate)。PC 不動。
      const { verts, count } = limited
        ? downsampleByOpacity(raw.verts, raw.count, targetSplats)
        : raw
      const mesh = await vertsToSplatMesh(verts, count)
      mesh.quaternion.set(1, 0, 0, 0)   // Spark splat 為 y-down,180°/X 翻正(同 DualgsViewer)
      mesh.position.set(0, 0, 0)
      return mesh
    }

    // ══════════════════════════════════════════════════════════════════════
    // PC 路徑:全預載(保持原本程式碼行為)
    // ══════════════════════════════════════════════════════════════════════
    async function preloadAll(manifest, GFRAMES, CS) {
      let curDec = null, curClip = -1
      for (let i = 0; i < GFRAMES.length && !disposed; i++) {
        const gf = GFRAMES[i], c = Math.floor(gf / CS)
        if (c !== curClip) {
          if (curDec) { curDec.destroy(); curDec = null }
          const clip = manifest.clips.find(x => x.id === c)
          if (!clip) { console.warn(`clip ${c} 未匯出,跳過 gf ${gf}`); continue }
          log(`載入 clip ${c}…`)
          curDec = await createDecoder(clip, `${dataUrl}/${clip.dir}`, manifest, log, device)
          curClip = c
        }
        setProgress(((i + 1) / GFRAMES.length) * 100)
        const mesh = await buildFrameMesh(curDec, gf, CS)
        if (disposed) { mesh.dispose?.(); break }
        builtMeshes.push(mesh)
        framesRef.current.push(mesh)
      }
      if (curDec) { curDec.destroy(); curDec = null }
      if (device) { try { device.destroy() } catch (e) {} device = null }  // 全預載完即釋放 device
      if (disposed) return
      if (framesRef.current.length === 0) throw new Error('沒有可播放的幀')

      setTotalFrames(framesRef.current.length)
      setIsLoading(false)

      const playFps = Math.max(1, fpsProp)
      let base = performance.now()
      showMesh(framesRef.current[0])

      const animate = (time, frame) => {
        if (disposed) { if (enableXR) renderer.setAnimationLoop(null); return }
        if (!enableXR) animIdRef.current = requestAnimationFrame(animate)
        if (playingRef.current) {
          const total = framesRef.current.length
          const idx = Math.floor(((performance.now() - base) / 1000) * playFps) % total
          if (idx !== curIdxRef.current) { curIdxRef.current = idx; showMesh(framesRef.current[idx]) }
        }
        controls.update(); updateXrGrab(frame); renderer.render(scene, camera)
      }
      if (enableXR) renderer.setAnimationLoop(animate)
      else animIdRef.current = requestAnimationFrame(animate)
    }

    // ══════════════════════════════════════════════════════════════════════
    // 非 PC 路徑:串流 + 有限快取(記憶體固定,fps 上限 24)
    // ══════════════════════════════════════════════════════════════════════
    async function startStreaming(manifest, GFRAMES, CS) {
      const total = GFRAMES.length
      setTotalFrames(total)
      const playFps = Math.min(STREAM_FPS_CAP, Math.max(1, fpsProp))
      const MAX_CACHE = Math.max(3, maxCachedFrames)
      const PREFETCH = Math.min(MAX_CACHE - 2, 4)   // 往前預解碼的幀數

      // 確保指定 clip 的 decoder 存活(同時只留一個 —— decoder 參數本身就佔 ~百 MB)
      async function ensureDecoder(c) {
        if (streamCurDec && streamCurDec._clip === c) return streamCurDec
        if (streamCurDec) { streamCurDec.destroy(); streamCurDec = null }
        const clip = manifest.clips.find(x => x.id === c)
        if (!clip) throw new Error(`clip ${c} 未匯出`)
        log(`載入 clip ${c}…`)
        const dec = await createDecoder(clip, `${dataUrl}/${clip.dir}`, manifest, log, device)
        dec._clip = c
        streamCurDec = dec
        return dec
      }

      async function decodeIndex(i) {
        const gf = GFRAMES[i], c = Math.floor(gf / CS)
        const dec = await ensureDecoder(c)
        return buildFrameMesh(dec, gf, CS)
      }

      // pump:把 [center .. center+PREFETCH] 補齊,超出視窗的釋放。單一 in-flight。
      let pumping = false
      async function pump() {
        if (pumping || disposed) return
        pumping = true
        try {
          const center = curIdxRef.current
          const wanted = []
          for (let k = 0; k <= PREFETCH; k++) wanted.push((center + k) % total)

          for (const idx of wanted) {
            if (disposed) break
            if (!streamCache.has(idx)) {
              const mesh = await decodeIndex(idx)
              if (disposed) { mesh.dispose?.(); break }
              streamCache.set(idx, mesh)
              if (isLoading) setIsLoading(false)   // 第一幀就緒 → 收掉 loading
            }
          }

          // 驅逐視窗外的幀(保留 center 與 wanted)
          if (streamCache.size > MAX_CACHE) {
            const keepSet = new Set(wanted); keepSet.add(center)
            for (const [idx, mesh] of streamCache) {
              if (streamCache.size <= MAX_CACHE) break
              if (keepSet.has(idx)) continue
              if (mesh === curFrameMeshRef.current) continue
              modelAnchor.remove(mesh); mesh.dispose?.(); streamCache.delete(idx)
            }
          }
        } catch (e) {
          console.warn('stream pump 例外', e?.message || e)
        } finally {
          pumping = false
        }
      }

      const interval = 1000 / playFps
      let lastSwap = performance.now()

      const animate = (time, frame) => {
        if (disposed) { if (enableXR) renderer.setAnimationLoop(null); return }
        if (!enableXR) animIdRef.current = requestAnimationFrame(animate)

        // gated:只有下一幀已解碼且到了時間才前進 —— 解碼跟不上時寧可掉速也不跳幀
        if (playingRef.current) {
          const next = (curIdxRef.current + 1) % total
          if (streamCache.has(next) && (performance.now() - lastSwap) >= interval) {
            curIdxRef.current = next
            lastSwap = performance.now()
          }
        }
        const mesh = streamCache.get(curIdxRef.current)
        if (mesh) showMesh(mesh)

        pump()   // 非阻塞:持續往前補幀 + 釋放舊幀
        controls.update(); updateXrGrab(frame); renderer.render(scene, camera)
      }

      await pump()   // 先把起始視窗解出來,避免第一幀空白
      if (enableXR) renderer.setAnimationLoop(animate)
      else animIdRef.current = requestAnimationFrame(animate)
    }

    // ---- 共用前置:manifest / GFRAMES / device,再依裝置分流 ----
    async function run() {
      if (!navigator.gpu) throw new Error('此瀏覽器不支援 WebGPU(解碼端需要;算圖走 WebGL)')
      const manifest = await (await fetch(`${dataUrl}/manifest.json`, { cache: 'no-store' })).json()
      setAssetVersion(manifest.version || '')
      manifest._dataRoot = dataUrl
      const CS = manifest.clip_size
      let GFRAMES = gframesKey ? parseFrames(gframesKey) : null
      if (!GFRAMES) {
        GFRAMES = []
        for (const cl of manifest.clips) { const b = cl.id * CS; for (let k = 0; k < CS; k++) GFRAMES.push(b + k) }
      }
      device = await createSharedDevice(log, 512 << 20, (text, sw) => { if (!disposed) setGpuInfo({ text, sw }) })
      if (disposed) { try { device.destroy() } catch (e) {} ; return }

      if (limited) await startStreaming(manifest, GFRAMES, CS)
      else await preloadAll(manifest, GFRAMES, CS)
    }

    run().catch((e) => { console.error(e); if (!disposed) { setError(e.message); setIsLoading(false) } })

    // ---- Cleanup ----
    return () => {
      disposed = true
      resizeObserver.disconnect()
      if (animIdRef.current !== null) cancelAnimationFrame(animIdRef.current)
      if (enableXR) renderer.setAnimationLoop(null)
      controls.dispose()
      for (const m of builtMeshes) { try { m.dispose?.() } catch (e) {} }
      for (const [, m] of streamCache) { try { m.dispose?.() } catch (e) {} }
      streamCache.clear()
      framesRef.current = []
      curFrameMeshRef.current = null
      if (streamCurDec) { try { streamCurDec.destroy() } catch (e) {} streamCurDec = null }
      renderer.dispose()
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement)
      if (device) { try { device.destroy() } catch (e) {} }
    }
  }, [dataUrl, gframesKey, keep, fpsProp, enableXR, maxCachedFrames, targetSplats])

  // ── Render(UI 從簡:載入條 + XR 按鈕 + AR overlay)──
  return (
    <div className={className} style={{ position: 'relative', width: '100%', height: '100%', background: '#000' }}>
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />

      {/* GPU 資訊 + 目前模式(串流 24fps / 全預載)。軟體模擬時轉紅警示。 */}
      {gpuInfo && !error && (
        <div style={{
          position: 'absolute', top: 8, left: 8, zIndex: 15,
          fontSize: 12, fontFamily: 'monospace',
          background: 'rgba(0,0,0,0.55)', padding: '4px 8px', borderRadius: 4,
          color: gpuInfo.sw ? '#f55' : '#5f5', pointerEvents: 'none',
        }}>
          {gpuInfo.text}
          {' · '}
          <span style={{ color: limited ? '#ffcc55' : '#8bd' }}>
            {limited
              ? `串流 ${STREAM_FPS_CAP}fps${targetSplats ? ` · ≤${Math.round(targetSplats / 1000)}k` : ''}`
              : '全預載'}
          </span>
        </div>
      )}

      <div
        ref={arOverlayRef}
        id="overlay"
        style={{ position: 'absolute', inset: 0, pointerEvents: xrActive ? 'auto' : 'none', color: '#fff', zIndex: xrActive ? 40 : -1 }}
      >
        {xrActive && arStatus === 'searching' && <div style={arHint}>移動裝置掃描平面…</div>}
        {xrActive && arStatus === 'ready' && <div style={arHint}>點擊圓圈放置模型</div>}
        {xrActive && arStatus === 'placed' && (
          <ModelTransformPanel modelAnchorRef={modelAnchorRef} inAR />
        )}
      </div>

      {isLoading && !error && (
        <div style={overlayCenter}>
          <div style={{ color: '#fff', fontSize: 14 }}>Loading {Math.round(progress)}%</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontFamily: 'monospace' }}>{status}</div>
        </div>
      )}
      {error && <div style={{ ...overlayCenter, color: '#f44' }}>Error: {error}</div>}

      {showUi && !isLoading && !error && (
        <div style={{ position: 'absolute', right: 16, bottom: 16, zIndex: 10 }}>
          <XRButton
            enableXR={enableXR} xrSupport={xrSupport} xrActive={xrActive}
            onEnter={enterXR} onExit={exitXR}
            ctrlBtn={{ background: 'rgba(255,255,255,0.1)', color: '#fff', padding: '6px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.18)', cursor: 'pointer' }}
          />
        </div>
      )}
    </div>
  )
})

const arHint = {
  position: 'absolute', top: 24, left: '50%', transform: 'translateX(-50%)',
  padding: '10px 16px', borderRadius: 999, background: 'rgba(18,18,18,0.7)',
  border: '1px solid rgba(255,255,255,0.18)', color: '#fff', fontSize: 14, whiteSpace: 'nowrap', pointerEvents: 'none',
}
const overlayCenter = {
  position: 'absolute', inset: 0, zIndex: 20,
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
  background: 'rgba(0,0,0,0.4)',
}

export default ClipGStreamViewerXR
